import { Injectable } from "@angular/core";
import { Apollo, gql } from "apollo-angular";
import { HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, map, Observable, switchMap, tap, timer } from "rxjs";
import { ApolloQueryResult } from "@apollo/client/core";
import { Charge, ChargeItemDocumentType, ChargeStatus } from "../../../features/charges/models/charge.models";
import { BadgeVariant } from "../../../features/charges/ui/badge/badge.component";

export class ChargeStatusMapper {
  static mapStatus(status: string): ChargeStatus {
    if (status === "completed") {
      return ChargeStatus.PAGADO;
    }
    if (status.includes("canceled")) {
      return ChargeStatus.NOPAGADO;
    }
    return ChargeStatus.PENDIENTE;
  }
}

export interface OnlinecheckoutLineItemProduct {
  category_id: string;
  description: string;
  name: string;
  picture_url: string;
  sku: string;
  unit_amount: string;
}

export interface OnlinecheckoutLineItem {
  amount: number;
  quantity: number;
  product: OnlinecheckoutLineItemProduct;
}

export interface Onlinecheckout {
  id: string;
  status: string;
  status_reason?: string;
  source: string;
  merchant_id: string;
  payment_intent_id?: string;
  payment_link_url?: string;
  order_number?: string;
  created_at: string;
  amount: number;
  line_items?: OnlinecheckoutLineItem[];
  created_at_time_zone?: string;
  payment_status?: string;
}

@Injectable({
  providedIn: "root",
})
export class ChargeService {
  private chargesSubject = new BehaviorSubject<Charge[] | null>(null);

  private loadingSubject = new BehaviorSubject<boolean>(true);
  loading$ = this.loadingSubject.asObservable();

  constructor(private apollo: Apollo) {}

  charges$: Observable<Charge[]> = this.chargesSubject.asObservable().pipe(map((charges) => charges || []));

  private queryCharges(): void {
    const authToken = localStorage.getItem("auth.access_token");
    const headers = new HttpHeaders().set("Authorization", `Bearer ${authToken}`);

    timer(2200)
    .pipe(
      switchMap(() =>
        this.apollo
          .query({
            query: gql`
              query cobros($merchantId: String!, $source: String!, $timeZone: String!) {
                onlinecheckout(merchant_id: $merchantId, time_zone: $timeZone, source: $source) {
                  items {
                    id
                    amount
                    status
                    created_at_time_zone
                    payment_intent_id
                    payment_status
                    order_number
                    merchant_id
                    status_reason
                    payment_link_url
                    line_items {
                      amount
                      quantity
                      product {
                        category_id
                        description
                        name
                        picture_url
                        sku
                        unit_amount
                      }
                    }
                  }
                }
              }
            `,
            variables: {
              merchantId: localStorage.getItem("auth.merchant_id"),
              timeZone: localStorage.getItem("auth.time_zone"),
              source: "billing",
            },
            context: {
              headers: headers,
            },
            fetchPolicy: "network-only",
          })
          .pipe(
            tap((result: ApolloQueryResult<any>) => {
              console.log("GraphQL result:", result);
              console.log("GraphQL data:", result.data);
              this.loadingSubject.next(false);
              if (result.data?.onlinecheckout?.items) {
                const mappedCharges: Charge[] = result.data.onlinecheckout.items.map((charge: any) => {
                  const items = charge.line_items.map((lineItem: any) => {
                    const documentType = lineItem.product.name?.split("|")?.[0]?.trim() || ChargeItemDocumentType.Factura;
                    const documentNumber = lineItem.product.name?.split("|")?.[1]?.trim();
                    return {
                      documentType,
                      documentNumber,
                      description: lineItem.product.description,
                      amount: lineItem.amount,
                    };
                  });
                  const newChage: Charge = {
                    id: charge.id,
                    date: new Date(charge.created_at_time_zone || charge.created_at),
                    amount: charge.amount,
                    type: "Cobro por documentos",
                    status: ChargeStatusMapper.mapStatus(charge.status),
                    status_reason: charge.status_reason,
                    payment_link_url: charge.payment_link_url,
                    merchant_id: charge.merchant_id,
                    order_number: charge.order_number,
                    payment_intent_id: charge.payment_intent_id,
                    items: items || [],
                  };
                  return newChage;
                });

                console.log("Items found:", mappedCharges);
                this.chargesSubject.next(mappedCharges);
              } else {
                console.log("No items found, setting empty array");
                this.chargesSubject.next([]);
              }
            })
          )
      )
    )
    .subscribe();
  }

  public listAllCharges(): Observable<Charge[]> {
    this.queryCharges();
    return this.charges$;
  }
}

export function getBadgeVariant(charge: Charge): BadgeVariant {
  switch (charge.status) {
    case ChargeStatus.PAGADO:
      return BadgeVariant.success;
    case ChargeStatus.PENDIENTE:
      return BadgeVariant.warning;
    case ChargeStatus.NOPAGADO:
      return BadgeVariant.error;
    case ChargeStatus.ANULADO:
    default:
      return BadgeVariant.neutral;
  }
}
